import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { skillsApi, socialApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import StarButton from '../components/StarButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, ChevronRight, Download, Copy, Check,
  Eye, EyeOff, Clock, FileText, Zap, BookOpen, Code,
  Share2, Trash2, Edit, MoreVertical, Lock, Link2, Globe, FileEdit, LayoutGrid
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { StructuredContent } from '../components/StructuredContent';
import { CategoryModal } from '../components/CategoryModal';
import { parseMarkdownToSections } from '../utils/markdownParser';

const SkillDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedPrompt, setCopiedPrompt] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  // Check if current user is the owner
  const isOwner = user && skill && user.id === skill.user_id;

  // Status badge configuration
  const statusConfig = {
    DRAFT: {
      label: 'Draft',
      icon: <FileEdit className="h-3 w-3" />,
      className: 'bg-gray-100 text-gray-600 dark:bg-dark-bg-tertiary dark:text-gray-400 border-gray-300'
    },
    PRIVATE: {
      label: 'Private',
      icon: <Lock className="h-3 w-3" />,
      className: 'bg-gray-100 text-gray-700 dark:bg-dark-bg-tertiary dark:text-gray-300 border-gray-300 dark:border-[#2e2e2d]'
    },
    UNLISTED: {
      label: 'Unlisted',
      icon: <Link2 className="h-3 w-3" />,
      className: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 border-orange-300'
    },
    PUBLIC: {
      label: 'Public',
      icon: <Globe className="h-3 w-3" />,
      className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-300'
    }
  };

  useEffect(() => {
    loadSkill();
  }, [id]);

  const loadSkill = async () => {
    try {
      setLoading(true);
      const res = await skillsApi.get(id);
      console.log('=== SKILL LOADED ===');
      console.log('Full skill object:', res.data);
      console.log('Category field:', res.data.category);
      console.log('Category_id field:', res.data.category_id);
      console.log('Category type:', typeof res.data.category);
      setSkill(res.data);
    } catch (error) {
      console.error('Failed to load skill', error);
      toast.error('Failed to load skill');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPrompt = (prompt, index) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(index);
    toast.success('Prompt copied!');
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const handleDownloadSkill = async () => {
    try {
      // If skill has a package_zip, download the original ZIP package
      if (skill.package_zip || skill.source === 'upload') {
        const response = await skillsApi.downloadPackage(skill.id);
        
        // Check if we got a valid blob response
        if (response.data && response.data.size > 0) {
          const blob = response.data;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${skill.slug || skill.name.toLowerCase().replace(/\s+/g, '-')}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success('Skill package downloaded!');
          return;
        }
      }
      
      // Fallback: Download just the skill markdown file
      const content = skill.skill_file_content || skill.skill_md || '';
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${skill.name.toLowerCase().replace(/\s+/g, '-')}.skill.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Skill file downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download skill');
    }
  };

  // Share functionality
  const getSkillUrl = () => {
    return window.location.href;
  };

  const handleCopyLink = async () => {
    const url = getSkillUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy link', error);
      toast.error('Failed to copy link');
    }
  };

  const handleShareTwitter = () => {
    const url = getSkillUrl();
    const text = `Check out this Claude Skill: ${skill.name}`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
  };

  const handleShareLinkedIn = () => {
    const url = getSkillUrl();
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=520');
  };

  const handleShareFacebook = () => {
    const url = getSkillUrl();
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
  };

  // Visibility toggle functionality
  const handleToggleVisibility = async () => {
    const newVisibility = skill.visibility === 'PUBLIC' ? 'PRIVATE' : 'PUBLIC';
    
    try {
      await skillsApi.updateVisibility(skill.id, newVisibility);
      toast.success(`Skill is now ${newVisibility.toLowerCase()}`);
      // Refresh to show updated state
      window.location.reload();
    } catch (error) {
      console.error('Failed to update visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleUpdateVisibility = async (newVisibility) => {
    try {
      await skillsApi.updateVisibility(skill.id, newVisibility);
      toast.success(`Visibility updated to ${newVisibility}`);
      loadSkill();
    } catch (error) {
      console.error('Failed to update visibility', error);
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
  };

  const handleDelete = async () => {
    try {
      await skillsApi.delete(skill.id);
      toast.success('Skill deleted successfully');
      navigate('/my-skills');
    } catch (error) {
      console.error('Failed to delete skill', error);
      toast.error('Failed to delete skill');
    }
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    
    try {
      await skillsApi.update(skill.id, { name: editedTitle.trim() });
      toast.success('Skill title updated');
      setIsEditingTitle(false);
      loadSkill();
    } catch (error) {
      console.error('Failed to update title', error);
      toast.error('Failed to update title');
    }
  };

  const handleSaveCategory = async (category) => {
    try {
      console.log('Updating skill', skill.id, 'with category:', category);
      const response = await skillsApi.update(skill.id, { category });
      console.log('Category update response:', response);
      console.log('Updated skill data:', response.data);
      
      // Update local skill state with the response
      setSkill(response.data);
      
      toast.success(`Category updated to ${category}`);
      setCategoryModalOpen(false);
    } catch (error) {
      console.error('Failed to update category', error);
      console.error('Error details:', error.response);
      toast.error('Failed to update category');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <GlassCard className="h-64 skeleton"></GlassCard>
        <GlassCard className="h-96 skeleton"></GlassCard>
      </div>
    );
  }

  if (!skill) return null;

  return (
    <div className="space-y-8">
      {/* Back Button & Breadcrumbs */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-gray dark:text-dark-text-secondary hover:text-text-dark dark:hover:text-dark-text mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        
        <div className="flex items-center gap-2 text-sm text-text-gray dark:text-dark-text-secondary">
          <Link to="/browse" className="hover:text-primary transition-colors">Browse</Link>
          {(skill.category_id || skill.category) && (
            <>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/categories/${skill.category_id || skill.category}`} className="hover:text-primary transition-colors">
                {typeof skill.category === 'object' ? skill.category.name : (skill.category || skill.category_id)}
              </Link>
            </>
          )}
          <ChevronRight className="h-4 w-4" />
          <span className="text-text-dark dark:text-dark-text font-medium">{skill.name}</span>
        </div>
      </div>

      {/* Hero Section */}
      <GlassCard variant="strong" className="p-8">
        <div className="flex justify-between items-start mb-6">
          <div className="flex-1 max-w-3xl">
            {/* Title + Slug */}
            {isOwner && isEditingTitle ? (
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-[#2e2e2d] rounded-lg px-3 py-1 flex-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') {
                      setIsEditingTitle(false);
                      setEditedTitle(skill.name);
                    }
                  }}
                />
                <Button onClick={handleSaveTitle} className="bg-green-600 hover:bg-green-700 text-white">
                  <Check className="w-4 h-4" />
                </Button>
                <Button 
                  onClick={() => {
                    setIsEditingTitle(false);
                    setEditedTitle(skill.name);
                  }} 
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text">
                  {skill.name}
                </h1>
                {isOwner && (
                  <button
                    onClick={() => {
                      setEditedTitle(skill.name);
                      setIsEditingTitle(true);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-dark-bg-hover rounded-lg transition-colors"
                    title="Edit title"
                  >
                    <Edit className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mb-4">
              {skill.slug}
            </p>
            
            {/* Badges: Category + Status (inline) */}
            <div className="flex items-center gap-2 mb-4">
              {(skill.category || skill.category_id) && (
                <Badge className="bg-primary text-white border-0 font-medium">
                  {typeof skill.category === 'object' ? skill.category.name : (skill.category || skill.category_id)}
                </Badge>
              )}
              
              {/* Status Badge - Only for owner */}
              {isOwner && (() => {
                let status;
                if (!skill.is_published || skill.visibility === 'DRAFT') {
                  status = 'DRAFT';
                } else {
                  status = skill.visibility || 'PUBLIC';
                }
                const config = statusConfig[status];
                return config ? (
                  <Badge className={`flex items-center gap-1 border ${config.className}`}>
                    {config.icon}
                    <span>{config.label}</span>
                  </Badge>
                ) : null;
              })()}
            </div>
            <p className="text-lg text-text-gray dark:text-dark-text-secondary mb-6">
              {skill.description}
            </p>
            
            {/* Creator Info */}
            <div className="flex items-center gap-3 mb-6">
              <Avatar className="h-10 w-10">
                <AvatarImage src={skill.creator?.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {skill.creator?.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-text-dark dark:text-dark-text">
                  {skill.creator?.display_name || skill.creator?.username}
                </p>
                <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                  @{skill.creator?.username}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 text-text-gray dark:text-dark-text-secondary">
              <div className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                <span>{skill.install_count || 0} downloads</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Updated {new Date(skill.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {/* Show different actions for owner vs visitor */}
            {isOwner ? (
              <>
                {/* Download - PRIMARY BUTTON (always enabled) */}
                <Button
                  onClick={handleDownloadSkill}
                  className="gradient-primary text-white rounded-xl"
                  title="Download skill package"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>

                {/* Owner Management Dropdown - SECONDARY */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-xl glass">
                      <MoreVertical className="mr-2 h-4 w-4" />
                      Manage
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glass-strong w-48">
                    <DropdownMenuItem onClick={() => navigate(`/skills/${skill.id}/edit`)} className="cursor-pointer">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem onClick={() => setCategoryModalOpen(true)} className="cursor-pointer">
                      <LayoutGrid className="h-4 w-4 mr-2" />
                      Category
                    </DropdownMenuItem>
                    
                    {/* Visibility Submenu - always shown for owners */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        <Eye className="h-4 w-4 mr-2" />
                        Visibility
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-[#2e2e2d] shadow-lg min-w-[200px]">
                          <DropdownMenuItem
                            onClick={() => handleUpdateVisibility('DRAFT')}
                            className={`cursor-pointer ${skill.visibility === 'DRAFT' ? 'bg-accent' : ''}`}
                          >
                            <FileEdit className="h-4 w-4 mr-2 text-gray-600" />
                            <div className="flex-1">
                              <div className="font-medium">Draft</div>
                              <div className="text-xs text-gray-500">Only you</div>
                            </div>
                            {skill.visibility === 'DRAFT' && <Check className="h-4 w-4 ml-2 text-green-500" />}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleUpdateVisibility('PRIVATE')}
                            className={`cursor-pointer ${skill.visibility === 'PRIVATE' ? 'bg-accent' : ''}`}
                          >
                            <Lock className="h-4 w-4 mr-2 text-blue-600" />
                            <div className="flex-1">
                              <div className="font-medium">Private</div>
                              <div className="text-xs text-gray-500">Only you</div>
                            </div>
                            {skill.visibility === 'PRIVATE' && <Check className="h-4 w-4 ml-2 text-green-500" />}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleUpdateVisibility('UNLISTED')}
                            className={`cursor-pointer ${skill.visibility === 'UNLISTED' ? 'bg-accent' : ''}`}
                          >
                            <Link2 className="h-4 w-4 mr-2 text-amber-600" />
                            <div className="flex-1">
                              <div className="font-medium">Unlisted</div>
                              <div className="text-xs text-gray-500">Anyone with link</div>
                            </div>
                            {skill.visibility === 'UNLISTED' && <Check className="h-4 w-4 ml-2 text-green-500" />}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleUpdateVisibility('PUBLIC')}
                            className={`cursor-pointer ${skill.visibility === 'PUBLIC' ? 'bg-accent' : ''}`}
                          >
                            <Globe className="h-4 w-4 mr-2 text-green-600" />
                            <div className="flex-1">
                              <div className="font-medium">Public</div>
                              <div className="text-xs text-gray-500">Everyone</div>
                            </div>
                            {skill.visibility === 'PUBLIC' && <Check className="h-4 w-4 ml-2 text-green-500" />}
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => setDeleteModalOpen(true)} 
                      className="cursor-pointer text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Share Button - SECONDARY */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-xl glass">
                      <Share2 className="mr-2 h-4 w-4" />
                      Share
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glass-strong w-48">
                    <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
                      {copiedLink ? (
                        <>
                          <Check className="h-4 w-4 mr-2 text-green-600" />
                          Link Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Link
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleShareTwitter} className="cursor-pointer">
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      Share on X
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareLinkedIn} className="cursor-pointer">
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      Share on LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareFacebook} className="cursor-pointer">
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Share on Facebook
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {/* Visitor Actions */}
                <StarButton 
                  skillId={skill.id} 
                  starCount={skill.star_count || 0}
                  size="default"
                  showCount={true}
                />
                
                {/* Share Button for Visitors */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-xl glass">
                      <Share2 className="mr-2 h-4 w-4" />
                      Share
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glass-strong w-48">
                    <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
                      {copiedLink ? (
                        <>
                          <Check className="h-4 w-4 mr-2 text-green-600" />
                          Link Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Link
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleShareTwitter} className="cursor-pointer">
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      Share on X
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareLinkedIn} className="cursor-pointer">
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      Share on LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareFacebook} className="cursor-pointer">
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Share on Facebook
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  onClick={handleDownloadSkill}
                  className="gradient-primary text-white rounded-xl"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </>
            )}
          </div>
        </div>
      </GlassCard>

      {/* Enhanced Tabbed Content - Dynamic based on bundled_resources */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full justify-start bg-gray-100 dark:bg-dark-bg-secondary p-1.5 rounded-xl mb-8 h-auto flex-wrap gap-2">
          <TabsTrigger 
            value="overview"
            className="flex items-center gap-2 px-5 py-3 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-dark-bg-tertiary data-[state=active]:shadow-md transition-all data-[state=active]:text-primary dark:data-[state=active]:text-primary font-medium"
          >
            <FileText className="w-4 h-4" />
            Overview
          </TabsTrigger>
          {skill.bundled_resources?.prompt_library && (
            <TabsTrigger 
              value="prompt-library"
              className="flex items-center gap-2 px-5 py-3 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-dark-bg-tertiary data-[state=active]:shadow-md transition-all data-[state=active]:text-purple-600 dark:data-[state=active]:text-purple-400 font-medium"
            >
              <Zap className="w-4 h-4" />
              Prompt Library
            </TabsTrigger>
          )}
          {skill.bundled_resources?.usage_guide && (
            <TabsTrigger 
              value="usage-guide"
              className="flex items-center gap-2 px-5 py-3 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-dark-bg-tertiary data-[state=active]:shadow-md transition-all data-[state=active]:text-green-600 dark:data-[state=active]:text-green-400 font-medium"
            >
              <FileText className="w-4 h-4" />
              Usage Guide
            </TabsTrigger>
          )}
          {skill.bundled_resources?.decision_guide && (
            <TabsTrigger 
              value="decision-guide"
              className="flex items-center gap-2 px-5 py-3 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-dark-bg-tertiary data-[state=active]:shadow-md transition-all data-[state=active]:text-amber-600 dark:data-[state=active]:text-amber-400 font-medium"
            >
              <Code className="w-4 h-4" />
              Decision Guide
            </TabsTrigger>
          )}
          <TabsTrigger 
            value="use-cases"
            className="flex items-center gap-2 px-5 py-3 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-dark-bg-tertiary data-[state=active]:shadow-md transition-all data-[state=active]:text-primary dark:data-[state=active]:text-primary font-medium"
          >
            <Zap className="w-4 h-4" />
            Use Cases
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab - Main SKILL.md content */}
        <TabsContent value="overview" className="space-y-6">
          <GlassCard variant="strong" className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-lg">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-2xl font-manrope font-bold text-text-dark dark:text-dark-text">
                Overview
              </h2>
            </div>
            <StructuredContent 
              sections={
                skill.bundled_resources?.overview?.content && typeof skill.bundled_resources.overview.content === 'string'
                  ? parseMarkdownToSections(skill.bundled_resources.overview.content)
                  : skill.bundled_resources?.overview || []
              } 
            />
          </GlassCard>
        </TabsContent>

        {/* Prompt Library Tab */}
        {skill.bundled_resources?.prompt_library && (
          <TabsContent value="prompt-library" className="space-y-4">
            <GlassCard variant="strong" className="p-8">
              <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-purple-600" />
                  {skill.bundled_resources.prompt_library.name}
                </h2>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  Pre-written prompts to get started quickly
                </p>
              </div>
              <StructuredContent 
                sections={
                  typeof skill.bundled_resources.prompt_library.content === 'string'
                    ? parseMarkdownToSections(skill.bundled_resources.prompt_library.content)
                    : skill.bundled_resources.prompt_library.content || []
                } 
              />
            </GlassCard>
          </TabsContent>
        )}

        {/* Usage Guide Tab */}
        {skill.bundled_resources?.usage_guide && (
          <TabsContent value="usage-guide" className="space-y-4">
            <GlassCard variant="strong" className="p-8">
              <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-green-600" />
                  {skill.bundled_resources.usage_guide.name}
                </h2>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Detailed instructions on how to use this skill
                </p>
              </div>
              <StructuredContent 
                sections={
                  typeof skill.bundled_resources.usage_guide.content === 'string'
                    ? parseMarkdownToSections(skill.bundled_resources.usage_guide.content)
                    : skill.bundled_resources.usage_guide.content || []
                } 
              />
            </GlassCard>
          </TabsContent>
        )}

        {/* Decision Guide Tab */}
        {skill.bundled_resources?.decision_guide && (
          <TabsContent value="decision-guide" className="space-y-4">
            <GlassCard variant="strong" className="p-8">
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <Code className="w-6 h-6 text-amber-600" />
                  {skill.bundled_resources.decision_guide.name}
                </h2>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  When and how to use this skill effectively
                </p>
              </div>
              <StructuredContent 
                sections={
                  typeof skill.bundled_resources.decision_guide.content === 'string'
                    ? parseMarkdownToSections(skill.bundled_resources.decision_guide.content)
                    : skill.bundled_resources.decision_guide.content || []
                } 
              />
            </GlassCard>
          </TabsContent>
        )}

        {/* Use Cases Tab */}
        <TabsContent value="use-cases" className="space-y-4">
          <GlassCard variant="strong" className="p-8">
            <div className="bg-primary/10 dark:bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Zap className="w-6 h-6 text-primary" />
                Use Cases
              </h2>
              <p className="text-sm text-primary/80 dark:text-primary/70">
                Practical applications and scenarios for this skill
              </p>
            </div>
            {skill.bundled_resources?.use_cases?.content ? (
              <StructuredContent 
                sections={
                  typeof skill.bundled_resources.use_cases.content === 'string'
                    ? parseMarkdownToSections(skill.bundled_resources.use_cases.content)
                    : skill.bundled_resources.use_cases.content || []
                } 
              />
            ) : (
              <div className="space-y-4">
                <p className="text-text-gray dark:text-dark-text-secondary">
                  No use cases available for this skill yet.
                </p>
                <div className="glass p-6 rounded-xl border-l-4 border-primary/30">
                  <h3 className="font-semibold text-lg text-text-dark dark:text-dark-text mb-3">
                    General Application
                  </h3>
                  <p className="text-text-gray dark:text-dark-text-secondary leading-relaxed">
                    {skill.description}
                  </p>
                </div>
              </div>
            )}
          </GlassCard>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Modal */}
      {isOwner && (
        <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <AlertDialogContent className="glass-strong border-2 border-gray-200 dark:border-[#2e2e2d] bg-white/95 dark:bg-dark-bg-primary/95 backdrop-blur-xl shadow-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-xl font-bold text-text-dark dark:text-dark-text">
                <div className="p-2 rounded-xl bg-destructive/10">
                  <Trash2 className="h-6 w-6 text-destructive" />
                </div>
                Delete Skill?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-text-gray dark:text-dark-text-secondary pt-4 text-base leading-relaxed">
                Are you absolutely sure you want to delete <span className="font-bold text-text-dark dark:text-dark-text">"{skill?.name}"</span>?
                <br /><br />
                <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                  <strong className="text-destructive">⚠️ Warning:</strong> This action cannot be undone. All stats and data associated with this skill will be permanently removed.
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 pt-6">
              <AlertDialogCancel className="rounded-xl border-2 hover:bg-gray-100 dark:hover:bg-dark-bg-hover">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="rounded-xl bg-red-600 text-white hover:bg-red-700 font-semibold shadow-lg border-0 flex items-center justify-center"
                style={{ backgroundColor: '#dc2626', color: 'white', whiteSpace: 'nowrap', minWidth: '140px' }}
              >
                <Trash2 className="h-4 w-4 mr-2" style={{ flexShrink: 0 }} />
                Delete Skill
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      
      {/* Category Modal */}
      <CategoryModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        currentCategory={skill?.category || skill?.category_id}
        onSave={handleSaveCategory}
      />
    </div>
  );
};

export default SkillDetailPage;